import React from "react";
import Editor from "@monaco-editor/react";

import { useTheme } from "@/hooks/use-theme";

interface WorkflowCodeEditorProps {
  code: string;
  onChange: (code: string) => void;
}

const WorkflowCodeEditor: React.FC<WorkflowCodeEditorProps> = ({ code, onChange }) => {
  const { isDark, theme } = useTheme();
  const [editorTheme, setEditorTheme] = React.useState(isDark ? "vs-dark" : "vs-light");
  const timeoutRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);

  React.useEffect(() => {
    setEditorTheme(isDark ? "vs-dark" : "vs-light");
  }, [theme, isDark]);

  // Cleanup timeout on unmount
  React.useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  const handleEditorChange = React.useCallback(
    (value: string | undefined) => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }

      timeoutRef.current = setTimeout(() => {
        if (value !== undefined) {
          onChange(value);
        }
      }, 5000);
    },
    [onChange],
  );

  return (
    <Editor
      data-id="workflow-code-editor"
      height="100%"
      language="yaml"
      options={{
        inlineSuggest: { enabled: true },
        fontSize: 12,
        formatOnType: false,
        autoClosingBrackets: "always",
        minimap: { enabled: false },
      }}
      theme={editorTheme}
      value={code}
      onChange={handleEditorChange}
    />
  );
};

export default WorkflowCodeEditor;
